import { DestroyRef, Injectable, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SentryFeedbackService {
  private readonly destroyRef = inject(DestroyRef);

  constructor(private authService: AuthenticationService) {
    // Only initialize if Sentry is enabled
    if (!environment.sentry?.enabled) {
      return;
    }

    // Set up user context tracking
    this.initializeUserTracking();
  }

  private initializeUserTracking(): void {
    combineLatest([
      this.authService.isAuthenticated$,
      this.authService.profile$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isAuthenticated, profile]) => {
        if (!isAuthenticated || !profile) {
          Sentry.setUser(null);
          Sentry.setContext('user_roles', null);
          return;
        }

        const user: Sentry.User = {
          id: profile.id ?? undefined,
          email: profile.emailAddress ?? undefined,
          username:
            profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`
              : profile.firstName ?? profile.lastName ?? undefined,
        };

        Sentry.setUser(user);

        if (profile.roles?.length) {
          Sentry.setContext('user_roles', { roles: profile.roles });
        } else {
          Sentry.setContext('user_roles', null);
        }
      });
  }

  /**
   * Manually set user context (useful for testing or special cases)
   */
  public setUserContext(user: Sentry.User): void {
    if (!environment.sentry?.enabled) {
      return;
    }
    Sentry.setUser(user);
  }

  /**
   * Clear user context
   */
  public clearUserContext(): void {
    if (!environment.sentry?.enabled) {
      return;
    }
    Sentry.setUser(null);
  }

  /**
   * Add custom context to Sentry
   */
  public setCustomContext(key: string, context: Record<string, unknown>): void {
    if (!environment.sentry?.enabled) {
      return;
    }
    Sentry.setContext(key, context);
  }
}
